import { apiClient } from '../lib/apiClient';
import { loggingService } from './loggingService';

export interface VersionConflictError {
  type: 'version_conflict';
  message: string;
  currentVersion: number;
  attemptedVersion: number;
  lastModifiedBy: string;
  lastModifiedAt: string;
  currentData: any;
}

export interface ActiveEditor {
  id: string;
  user_id: string;
  display_name: string;
  locked_at: string;
  last_activity_at: string;
  lock_expires_at: string;
}

export interface VersionHistoryEntry {
  id: string;
  version_number: number;
  operation: 'insert' | 'update' | 'delete';
  changed_by: string;
  changed_at: string;
  old_values: any;
  new_values: any;
  change_summary: string;
}

class VersionControlService {
  async checkVersionConflict(
    tableName: string,
    recordId: string,
    expectedVersion: number
  ): Promise<VersionConflictError | null> {
    try {
      return await apiClient.get<VersionConflictError | null>(
        `/version-control/${tableName}/${recordId}/conflict?expectedVersion=${expectedVersion}`
      );
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      loggingService.error('version_control', 'Version conflict check failed', error, { tableName, recordId });
      throw err;
    }
  }

  async updateWithVersionCheck<T extends { id: string; version: number }>(
    tableName: string,
    record: T,
    updates: Partial<T>,
    userId: string,
    changeSummary?: string
  ): Promise<{ data: T | null; conflict?: VersionConflictError }> {
    try {
      return await apiClient.post<{ data: T | null; conflict?: VersionConflictError }>(
        `/version-control/${tableName}/${record.id}/update`,
        { record, updates, userId, changeSummary }
      );
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      loggingService.error('version_control', 'Version-controlled update failed', error, { tableName, recordId: record.id });
      throw err;
    }
  }

  async recordVersionHistory(params: {
    tableName: string;
    recordId: string;
    operation: 'insert' | 'update' | 'delete';
    oldValues?: any;
    newValues?: any;
    changedBy: string;
    changeSummary?: string;
    orgId?: string;
  }): Promise<void> {
    try {
      await apiClient.post(`/version-control/${params.tableName}/${params.recordId}/history`, params);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      loggingService.error('version_control', 'Failed to record version history', error, { params });
    }
  }

  async getVersionHistory(
    tableName: string,
    recordId: string,
    limit: number = 50
  ): Promise<VersionHistoryEntry[]> {
    try {
      return await apiClient.get<VersionHistoryEntry[]>(
        `/version-control/${tableName}/${recordId}/history?limit=${limit}`
      );
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      loggingService.error('version_control', 'Failed to fetch version history', error, { tableName, recordId });
      return [];
    }
  }

  async acquireEditLock(
    tableName: string,
    recordId: string,
    userId: string,
    displayName: string,
    orgId: string
  ): Promise<{ success: boolean; conflict?: ActiveEditor }> {
    try {
      return await apiClient.post<{ success: boolean; conflict?: ActiveEditor }>(
        `/version-control/${tableName}/${recordId}/locks`,
        { userId, displayName, orgId }
      );
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      loggingService.error('version_control', 'Failed to acquire edit lock', error, { tableName, recordId });
      throw err;
    }
  }

  async releaseEditLock(tableName: string, recordId: string, userId: string): Promise<void> {
    try {
      await apiClient.delete(`/version-control/${tableName}/${recordId}/locks/${userId}`);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      loggingService.error('version_control', 'Failed to release edit lock', error, { tableName, recordId });
    }
  }

  async renewEditLock(tableName: string, recordId: string, userId: string): Promise<void> {
    try {
      await apiClient.patch(`/version-control/${tableName}/${recordId}/locks/${userId}/renew`, {});
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      loggingService.error('version_control', 'Failed to renew edit lock', error, { tableName, recordId });
    }
  }

  async getActiveEditors(tableName: string, recordId: string): Promise<ActiveEditor[]> {
    try {
      return await apiClient.get<ActiveEditor[]>(
        `/version-control/${tableName}/${recordId}/editors`
      );
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      loggingService.error('version_control', 'Failed to fetch active editors', error, { tableName, recordId });
      return [];
    }
  }

  async restoreVersion(
    tableName: string,
    recordId: string,
    versionNumber: number,
    userId: string
  ): Promise<{ success: boolean; data?: any }> {
    try {
      return await apiClient.post<{ success: boolean; data?: any }>(
        `/version-control/${tableName}/${recordId}/restore/${versionNumber}`,
        { userId }
      );
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      loggingService.error('version_control', 'Failed to restore version', error, { tableName, recordId, versionNumber });
      return { success: false };
    }
  }
}

export const versionControlService = new VersionControlService();
